import { PREVIEW_COMPANIES, PREVIEW_ACTOR_ID, PREVIEW_AUTH_USER_ID, PREVIEW_ROLES } from './flag';

// Deterministic mock datasets for Preview Mode. Realistic Georgian financial data; two
// companies with distinct figures so the Company Switcher visibly re-scopes. No computation
// of the real FIFO/aging engine — report RPC responses are canned, exact-decimal strings.
type Row = Record<string, unknown>;

const AGING = (a: number): Record<string, string> => ({
  notDue: (a * 0.4).toFixed(4),
  d1_7: (a * 0.15).toFixed(4),
  d8_15: (a * 0.12).toFixed(4),
  d16_30: (a * 0.13).toFixed(4),
  d31_60: (a * 0.1).toFixed(4),
  d61_90: (a * 0.06).toFixed(4),
  d91_plus: (a * 0.04).toFixed(4),
});

// 7.10-A (D-W): due-date provenance split. Deliberately a MIXTURE — the strip only renders when
// more than one source is present, and a preview that showed a single source would hide it.
const DUE_SOURCES = (a: number): Record<string, string> => ({
  policy: (a * 0.45).toFixed(4),
  legacyFallback: (a * 0.3).toFixed(4),
  openingLegacy: (a * 0.1).toFixed(4),
  queryTimeFallback: (a * 0.15).toFixed(4),
  persistedUnknown: '0.0000',
});
// Per-document provenance, cycled so the R4 table shows several kinds at once.
const DUE_SOURCE_BY_ROW = ['POLICY', 'LEGACY_FALLBACK', 'QUERY_TIME_FALLBACK', 'POLICY', 'LEGACY_FALLBACK', 'OPENING_LEGACY'];
const DUE_RUN_BY_ROW = ['POSTING', 'BACKFILL', null, 'POSTING', 'POSTING', null];

// 7.10-B (D-Z/D-AA): plan/review indicators per customer. A deliberate mixture — most customers
// have no plan (a plan is the exception), one is on schedule, one has slipped, one has a review.
const PLAN_BY_ROW = [
  { hasActivePlan: false, isBreached: false, delayDays: null, openReviewCount: 0 },
  { hasActivePlan: true, isBreached: false, delayDays: null, openReviewCount: 0 },
  { hasActivePlan: true, isBreached: true, delayDays: 23, openReviewCount: 0 },
  { hasActivePlan: false, isBreached: false, delayDays: null, openReviewCount: 1 },
  { hasActivePlan: true, isBreached: false, delayDays: null, openReviewCount: 2 },
  { hasActivePlan: false, isBreached: false, delayDays: null, openReviewCount: 0 },
];
const NO_PLAN_INDICATOR = { hasActivePlan: false, isBreached: false, delayDays: null, openReviewCount: 0 };
const planTotals = (n: number): Record<string, number> => ({
  customersOnPlan: PLAN_BY_ROW.slice(0, n).filter((p) => p.hasActivePlan).length,
  customersBreached: PLAN_BY_ROW.slice(0, n).filter((p) => p.isBreached).length,
  customersWithOpenReview: PLAN_BY_ROW.slice(0, n).filter((p) => p.openReviewCount > 0).length,
});

const CUSTOMERS = [
  'შპს გემა',
  'შპს ნიკორა',
  'ი/მ დავით ბერიძე',
  'შპს ორბი ჯგუფი',
  'შპს ვისოლ პეტროლიუმი',
  'შპს ელით ელექტრონიქსი',
];
const MANAGERS = ['ნინო კაპანაძე', 'გიორგი მაისურაძე', 'თამარ ლომიძე'];
const RECIPIENTS = ['ნინო კაპანაძე', 'გიორგი მაისურაძე', 'შპს რეფერალ პარტნიორი', 'ლევან ჩხეიძე'];

function uid(prefix: string, n: number): string {
  return `${prefix}${String(n).padStart(12, '0')}`.slice(0, 36).padEnd(36, '0');
}

interface CompanyData {
  readonly tables: Record<string, readonly Row[]>;
  readonly rpc: Record<string, (payload: Row) => unknown>;
  transition(batchId: string, to: string): void;
  alertAction(taskId: string, action: 'acknowledge' | 'done' | 'dismiss'): Row;
}

function build(companyId: string, factor: number): CompanyData {
  const scale = (n: number): string => (n * factor).toFixed(4);
  const scale2 = (n: number): string => (n * factor).toFixed(2);
  const nCust = factor > 0.9 ? 6 : 4;

  // ── commission tables (direct reads) ──
  const recipients: Row[] = RECIPIENTS.slice(0, factor > 0.9 ? 4 : 3).map((name, i) => ({
    id: uid('re', i + 1),
    company_id: companyId,
    recipient_type: ['sales_manager_user', 'sales_manager_user', 'referral_partner', 'intermediary_person'][i] ?? 'other',
    display_name: name,
    status: i === 3 ? 'inactive' : 'active',
    notes: i === 2 ? 'გარე რეფერალ პარტნიორი' : null,
    created_at: `2026-0${(i % 6) + 1}-1${i}T09:00:00Z`,
  }));

  // Slice C2: customer_id/recipient_id are real NOT NULL FKs on every live row (0028) — added
  // here (previously only the pre-joined display objects existed) so the RulesPage edit form's
  // customer/recipient Selects can pre-select in Preview Mode the same way they do against a
  // real database.
  const rules: Row[] = recipients.slice(0, 3).flatMap((r, i) =>
    CUSTOMERS.slice(0, 2).map((c, j) => ({
      id: uid('ru', i * 10 + j + 1),
      company_id: companyId,
      customer_id: uid('cu', j + 1),
      recipient_id: r['id'],
      // A NUMBER, not a string: `percent` is numeric(9,6) and this row imitates a direct table
      // select, which PostgREST serializes unquoted. The old `.toFixed(6)` string made Preview
      // Mode kinder than the real API and hid the "percent.trim is not a function" crash.
      percent: [2.5, 3, 1.5][i] ?? 2,
      valid_from: '2026-01-01',
      valid_to: j === 1 ? null : '2026-12-31',
      is_active: true,
      approved_at: `2026-01-05T10:00:00Z`,
      created_at: `2026-01-0${i + 2}T10:00:00Z`,
      customers: { name: c },
      commission_recipients: { display_name: r['display_name'] },
    })),
  );

  const events: Row[] = CUSTOMERS.slice(0, nCust).map((c, i) => {
    const base = 1000 + i * 340;
    const overridden = i === 1;
    return {
      id: uid('ev', i + 1),
      company_id: companyId,
      target_kind: i === 5 ? 'opening' : 'sale',
      customer_id: uid('cu', i + 1),
      snapshot_rate: '2.500000',
      calculated_base: scale(base),
      approved_base: overridden ? scale(base + 200) : scale(base),
      commission_amount: scale((overridden ? base + 200 : base) * 0.025),
      base_override_reason: overridden ? 'discount_correction' : null,
      status: ['CALCULATED', 'APPROVED', 'SETTLED', 'CALCULATED', 'PENDING_APPROVAL', 'REVERSED'][i] ?? 'CALCULATED',
      effective_date: `2026-06-${String(5 + i).padStart(2, '0')}`,
      created_at: `2026-06-${String(5 + i).padStart(2, '0')}T12:00:00Z`,
      customers: { name: c },
      commission_recipients: { display_name: RECIPIENTS[i % 3] },
      sales_documents: i === 5 ? null : { source_document_id: `SF-2026-${1000 + i}` },
    };
  });

  const overrideRows: Row[] = events
    .filter((e) => e['base_override_reason'] !== null)
    .map((e) => ({
      ...e,
      base_override_comment: 'ფასდაკლების კორექცია',
      base_overridden_by: PREVIEW_ACTOR_ID,
      base_approved_by: PREVIEW_ACTOR_ID,
      base_approved_at: '2026-06-07T09:00:00Z',
    }));

  const adjustments: Row[] = [
    { type: 'return', amt: -120, sys: true, settled: false },
    { type: 'correction', amt: 45, sys: false, settled: true },
    { type: 'refund', amt: -300, sys: false, settled: false },
    { type: 'allocation_recompute', amt: -15, sys: true, settled: false },
  ].map((a, i) => ({
    id: uid('aj', i + 1),
    company_id: companyId,
    adjustment_type: a.type,
    amount: scale(a.amt),
    reason: a.type === 'return' ? 'allocation reversed' : 'ხელით კორექცია',
    comment: i === 1 ? 'დამტკიცებული FM-ის მიერ' : null,
    created_by: a.sys ? null : PREVIEW_ACTOR_ID,
    settlement_line_id: a.settled ? uid('ln', 99) : null,
    effective_date: `2026-06-1${i}`,
    created_at: `2026-06-1${i}T14:00:00Z`,
    customers: { name: CUSTOMERS[i % nCust] },
    commission_recipients: { display_name: RECIPIENTS[i % 3] },
  }));

  const batches: Row[] = [
    { code: 'COMM-2026-06-001', status: 'Under Review', rec: 0 },
    { code: 'COMM-2026-06-002', status: 'Approved', rec: 1 },
    { code: 'COMM-2026-05-003', status: 'Paid', rec: 0 },
    { code: 'COMM-2026-06-004', status: 'Calculated', rec: 2 },
  ].map((b, i) => {
    const payable = 800 + i * 250;
    return {
      id: uid('ba', i + 1),
      company_id: companyId,
      code: b.code,
      status: b.status,
      period_from: '2026-06-01',
      period_to: '2026-06-30',
      calculated_total: scale(payable + 50),
      adjustments_total: scale(-50),
      payable_total: scale(payable),
      paid_total: b.status === 'Paid' ? scale(payable) : '0.0000',
      paid_at: b.status === 'Paid' ? '2026-07-01' : null,
      payment_document_ref: b.status === 'Paid' ? 'PAY-2026-0042' : null,
      approved_by: ['Approved', 'Paid'].includes(b.status) ? PREVIEW_ACTOR_ID : null,
      approved_at: ['Approved', 'Paid'].includes(b.status) ? '2026-06-28T11:00:00Z' : null,
      created_at: `2026-06-2${i}T08:00:00Z`,
      recipient_id: uid('re', b.rec + 1), // K-E-2c: the list's recipient filter (.eq)
      commission_recipients: { display_name: RECIPIENTS[b.rec] },
    };
  });

  const lines: Row[] = batches.flatMap((b, bi) =>
    [0, 1, 2].map((li) => ({
      id: uid('ln', bi * 10 + li + 1),
      company_id: companyId,
      batch_id: b['id'],
      source_type: li === 2 ? 'adjustment' : 'event',
      source_id: uid('ev', li + 1),
      // A NUMBER, like the real direct-table select (numeric(20,4) → JSON number, the D-185 lesson).
      amount: Number(scale(li === 2 ? -30 : 260 + li * 40)),
      included_at: `2026-06-2${bi}T09:0${li}:00Z`,
      // K-E-2c (0119): two lines share a customer so the per-customer grouping is visible.
      customer_id: uid('cu', li === 1 ? 2 : 1),
      customers: { name: CUSTOMERS[li === 1 ? 1 : 0] },
    })),
  );

  // ── identity tables ──
  // company_id must be this build's own company (not hardcoded to PREVIEW_COMPANIES[0]) now that
  // companies/company_settings below are single-row-scoped per company (mirrors RLS) — loadIdentity
  // resolves users -> companies for whichever company is currently active.
  // Slice 5B: user_roles is embedded directly on each users row (mirrors the commission tables'
  // pre-joined customers/commission_recipients convention above) — MockQuery has no real
  // relational-embed support, so `.from('users').select('...,user_roles(roles(code))')` needs the
  // shape already inline. Demo membership rows are distinct from the PREVIEW_ACTOR_ID identity
  // row (which stays administrator/active, matching PREVIEW_ROLES/loadIdentity).
  const roleEmbed = (codes: readonly string[]): Row[] => codes.map((code) => ({ roles: { code } }));
  const users: Row[] = [
    {
      id: PREVIEW_ACTOR_ID,
      company_id: companyId,
      auth_user_id: PREVIEW_AUTH_USER_ID,
      display_name: 'დემო ადმინისტრატორი',
      membership_status: 'active',
      version: 1,
      invited_at: null,
      activated_at: '2026-01-01T09:00:00Z',
      deactivated_at: null,
      created_at: '2026-01-01T09:00:00Z',
      user_roles: roleEmbed(PREVIEW_ROLES),
    },
    {
      id: uid('mb', 1),
      company_id: companyId,
      auth_user_id: uid('au', 1),
      display_name: 'ნინო კაპანაძე',
      membership_status: 'active',
      version: 1,
      invited_at: null,
      activated_at: '2026-02-10T09:00:00Z',
      deactivated_at: null,
      created_at: '2026-02-10T09:00:00Z',
      user_roles: roleEmbed(['sales_manager']),
    },
    {
      id: uid('mb', 2),
      company_id: companyId,
      auth_user_id: uid('au', 2),
      display_name: 'გიორგი მაისურაძე',
      membership_status: 'active',
      version: 1,
      invited_at: null,
      activated_at: '2026-03-05T09:00:00Z',
      deactivated_at: null,
      created_at: '2026-03-05T09:00:00Z',
      user_roles: roleEmbed(['finance_operator']),
    },
    {
      id: uid('mb', 3),
      company_id: companyId,
      auth_user_id: uid('au', 3),
      display_name: 'თამარ ლომიძე',
      membership_status: 'invited',
      version: 1,
      invited_at: '2026-06-01T09:00:00Z',
      activated_at: null,
      deactivated_at: null,
      created_at: '2026-06-01T09:00:00Z',
      user_roles: [],
    },
    {
      id: uid('mb', 4),
      company_id: companyId,
      auth_user_id: uid('au', 4),
      display_name: 'დავით ხუციშვილი',
      membership_status: 'inactive',
      version: 2,
      invited_at: null,
      activated_at: '2026-01-15T09:00:00Z',
      deactivated_at: '2026-05-01T09:00:00Z',
      created_at: '2026-01-15T09:00:00Z',
      user_roles: roleEmbed(['finance_manager']),
    },
  ];
  // Single-row tables, scoped to just this company — mirrors production RLS (sel_companies/
  // sel_company_settings, 0011) where exactly one row is ever visible. Two rows here would let
  // `.maybeSingle()` (no `.eq` filter — the RLS-scoped read pattern, see admin/api.ts) silently
  // return the wrong company's data regardless of which company is active.
  const previewName = PREVIEW_COMPANIES.find((c) => c.id === companyId)?.name ?? companyId;
  const previewIndex = PREVIEW_COMPANIES.findIndex((c) => c.id === companyId);
  const companiesTable: Row[] = [
    {
      id: companyId,
      name: previewName,
      tax_id: `20400${10000 + Math.max(previewIndex, 0)}`,
      base_currency: 'GEL',
      status: 'active',
      version: 1,
    },
  ];
  const companySettingsTable: Row[] = [
    {
      company_id: companyId,
      own_tax_id: companiesTable[0]!['tax_id'],
      default_payment_terms_days: 14,
      security_limits: {
        maxCompressedBytes: 52428800,
        maxDecompressedBytes: 262144000,
        maxRows: 100000,
        maxSheets: 20,
        maxCellTextLength: 50000,
      },
      system_cutover_date: null,
      opening_balance_as_of_date: null,
      version: 1,
    },
  ];
  const userRoles: Row[] = PREVIEW_ROLES.map((code) => ({ user_id: PREVIEW_ACTOR_ID, roles: { code } }));

  // Slice 5C-B: audit_events using the exact action/entity_type strings the admin mutation RPCs
  // actually write (0042-0045) — schema-valid, company-distinct (own actor/entity ids and
  // previewName), and covering every honest-fallback path the UI must exercise: an actor id
  // absent from this company's users set (deleted/inaccessible actor), and a real non-admin
  // action outside the known-admin set (post_sale_document, 0014_posting_functions.sql — the
  // real feed is unfiltered, most rows are not admin actions).
  const auditEvents: Row[] = [
    {
      id: uid('ae', 1),
      company_id: companyId,
      actor_user_id: PREVIEW_ACTOR_ID,
      action: 'admin_attach_membership',
      entity_type: 'users',
      entity_id: uid('mb', 3),
      old_json: null,
      new_json: { member_id: uid('mb', 3), status: 'invited' },
      reason: null,
      created_at: '2026-06-01T09:05:00Z',
    },
    {
      id: uid('ae', 2),
      company_id: companyId,
      actor_user_id: PREVIEW_ACTOR_ID,
      action: 'admin_set_member_status',
      entity_type: 'users',
      entity_id: uid('mb', 4),
      old_json: { status: 'active', version: 1 },
      new_json: { member_id: uid('mb', 4), status: 'inactive', version: 2 },
      reason: 'თანამშრომელი გაათავისუფლეს',
      created_at: '2026-05-01T09:10:00Z',
    },
    {
      id: uid('ae', 3),
      company_id: companyId,
      actor_user_id: PREVIEW_ACTOR_ID,
      action: 'admin_set_member_roles',
      entity_type: 'users',
      entity_id: uid('mb', 2),
      old_json: { role_codes: ['finance_operator'], version: 1 },
      new_json: { member_id: uid('mb', 2), role_codes: ['finance_operator', 'management'], version: 2 },
      reason: null,
      created_at: '2026-04-10T11:20:00Z',
    },
    {
      id: uid('ae', 4),
      company_id: companyId,
      actor_user_id: PREVIEW_ACTOR_ID,
      action: 'admin_update_company',
      entity_type: 'companies',
      entity_id: companyId,
      old_json: { name: previewName, version: 1 },
      new_json: { name: previewName, version: 2 },
      reason: null,
      created_at: '2026-03-15T08:45:00Z',
    },
    {
      id: uid('ae', 5),
      company_id: companyId,
      actor_user_id: PREVIEW_ACTOR_ID,
      action: 'admin_update_settings',
      entity_type: 'company_settings',
      entity_id: companyId,
      old_json: { default_payment_terms_days: 30, version: 1 },
      new_json: { default_payment_terms_days: 14, version: 2 },
      reason: null,
      created_at: '2026-02-20T14:00:00Z',
    },
    {
      // Missing/deleted actor — id deliberately absent from this company's own `users` array
      // above, so the UI's fallback (honest UUID retained, never a fabricated name) is exercised.
      id: uid('ae', 6),
      company_id: companyId,
      actor_user_id: uid('mb', 99),
      action: 'admin_set_member_status',
      entity_type: 'users',
      entity_id: uid('mb', 1),
      old_json: { status: 'invited', version: 1 },
      new_json: { member_id: uid('mb', 1), status: 'active', version: 2 },
      reason: null,
      created_at: '2026-02-01T10:00:00Z',
    },
    {
      // Non-admin action — proves the feed is a real, un-filtered audit_events read: the UI's
      // unknown-action fallback must never invent a translation for this. Exact contract from
      // 0014_posting_functions.sql fn_post_sale_document: action/entity_type strings, and
      // new_json matching jsonb_build_object('ok', true, 'sales_document_id', v_doc_id,
      // 'ledger_entry_id', v_le_id, 'document_type', v_type) exactly — v_type is constrained to
      // 'SALE'/'RETURN' only (the function raises INVALID_FINANCIAL_EFFECT otherwise); no
      // old_json column is written (null). p_actor is a plain parameter with no non-null
      // guarantee at the schema or function level — this row uses a representative real actor
      // (a company member) without claiming the migration enforces that.
      id: uid('ae', 7),
      company_id: companyId,
      actor_user_id: uid('mb', 2),
      action: 'post_sale_document',
      entity_type: 'sales_document',
      entity_id: uid('sd', 1),
      old_json: null,
      new_json: { ok: true, sales_document_id: uid('sd', 1), ledger_entry_id: uid('le', 9), document_type: 'SALE' },
      reason: null,
      created_at: '2026-01-10T07:30:00Z',
    },
  ];

  // Import Latest-Posted Dates (Phase 6.11): SALE/RETURN/INCOMING_PAYMENT rows with distinct
  // effective_date values per company (factor-scaled) so the Company Switcher visibly re-scopes
  // the watermark. RETURN's later date must NOT surface as the "რეალიზაცია" watermark — the
  // approved business decision scopes sales strictly to entry_type='SALE'.
  const ledgerEntries: Row[] = [
    { id: uid('le', 201), company_id: companyId, entry_type: 'SALE', effective_date: factor > 0.9 ? '2026-07-15' : '2026-07-09' },
    { id: uid('le', 202), company_id: companyId, entry_type: 'SALE', effective_date: factor > 0.9 ? '2026-07-08' : '2026-07-02' },
    { id: uid('le', 203), company_id: companyId, entry_type: 'RETURN', effective_date: factor > 0.9 ? '2026-07-18' : '2026-07-13' },
    { id: uid('le', 204), company_id: companyId, entry_type: 'INCOMING_PAYMENT', effective_date: factor > 0.9 ? '2026-07-12' : '2026-07-06' },
    { id: uid('le', 205), company_id: companyId, entry_type: 'INCOMING_PAYMENT', effective_date: factor > 0.9 ? '2026-07-04' : '2026-06-29' },
  ];

  // Slice P1-A — the customer registry (CustomersPage). users(display_name) is inlined per the
  // MockQuery no-embed convention above; ნინო კაპანაძე is this fixture's sales_manager.
  const customersTable: Row[] = CUSTOMERS.slice(0, 6).map((name, i) => ({
    id: uid('cu', i + 1),
    company_id: companyId,
    name,
    tax_id: ['405185222', '01024071816', '204954321', '406112233', '01008044455', '215678901'][i],
    payment_terms_days: [30, null, 14, 45, null, 0][i],
    status: i === 5 ? 'inactive' : 'active',
    created_at: `2026-0${(i % 6) + 1}-15T09:00:00Z`,
    // P1-H-2: the raw manager id rides along with the inlined embed — the edit form preselects it.
    current_sales_manager_user_id: i % 2 === 0 ? uid('mb', 1) : null,
    users: i % 2 === 0 ? { display_name: 'ნინო კაპანაძე' } : null,
  }));

  // Slice P3 — committed opening balances (OpeningBalancesPage): customers(name) inlined per the
  // MockQuery no-embed convention. One receivable, one advance, one reversed (history row).
  const openingDocumentsTable: Row[] = [
    {
      id: uid('od', 1), company_id: companyId, customer_id: uid('cu', 1),
      balance_type: 'receivable', open_amount: '4200.00',
      external_document_date: '2026-06-10', due_date: '2026-07-10',
      comment: 'საწყისი ნაშთი', status: 'ACTIVE', created_at: '2026-06-30T09:00:00Z',
      customers: { name: CUSTOMERS[0] },
    },
    {
      id: uid('od', 2), company_id: companyId, customer_id: uid('cu', 2),
      balance_type: 'advance', open_amount: '650.00',
      external_document_date: '2026-06-20', due_date: '2026-06-20',
      comment: null, status: 'ACTIVE', created_at: '2026-06-30T09:05:00Z',
      customers: { name: CUSTOMERS[1] },
    },
    {
      id: uid('od', 3), company_id: companyId, customer_id: uid('cu', 3),
      balance_type: 'receivable', open_amount: '900.00',
      external_document_date: '2026-06-01', due_date: '2026-06-25',
      comment: 'შესწორებამდე', status: 'REVERSED', created_at: '2026-06-30T09:10:00Z',
      customers: { name: CUSTOMERS[2] },
    },
  ];

  const tables: Record<string, Row[]> = {
    commission_recipients: recipients,
    customer_commission_rules: rules,
    commission_events: overrideRowsMerge(events, overrideRows),
    commission_adjustments: adjustments,
    commission_settlement_batches: batches,
    commission_settlement_lines: lines,
    customers: customersTable,
    opening_documents: openingDocumentsTable,
    users,
    companies: companiesTable,
    company_settings: companySettingsTable,
    user_roles: userRoles,
    audit_events: auditEvents,
    import_batches: [],
    ledger_entries: ledgerEntries,
    // P1-H-3: current status of the statement fixture's two sales (paid/unpaid export subsets).
    sales_documents: [
      { id: uid('sd', 1001), status: 'PAID', source_document_id: 'SF-2026-1001', document_date: '2026-06-01' },
      { id: uid('sd', 1044), status: 'PARTIALLY_PAID', source_document_id: 'SF-2026-1044', document_date: '2026-06-15' },
    ],
    // D-212: one ACTIVE recovery plan (customer 1) so the plans page and its Word agreement can be
    // exercised in Preview Mode. Its installments come from recoveryPlanFixture.ts; the covered
    // documents below sum to the principal, exactly as 0087's junction guarantees.
    installment_templates: [
      { id: uid('tp', 1), name: '3 თვე — 50/30/20%', mode: 'PERCENTAGE', installment_count: 3, total_amount: null, created_at: '2026-06-20T09:00:00Z' },
    ],
    recovery_plans: [
      {
        id: uid('pl', 1), customer_id: uid('cu', 1), currency: 'GEL', principal_amount: '3600.5000', template_id: uid('tp', 1),
        status: 'ACTIVE', superseded_by_plan_id: null, created_at: '2026-07-01T09:00:00Z', activated_at: '2026-07-01T09:05:00Z',
      },
    ],
    recovery_plan_documents: [
      { id: uid('pd', 1), plan_id: uid('pl', 1), sale_document_id: uid('sd', 1001), opening_document_id: null, open_amount: '1400.5000' },
      { id: uid('pd', 2), plan_id: uid('pl', 1), sale_document_id: uid('sd', 1044), opening_document_id: null, open_amount: '2200.0000' },
    ],
  };

  // ── report RPC responses (canned) ──
  const page = { returned: nCust, limit: 50, hasMore: false };
  const r1rows = CUSTOMERS.slice(0, nCust).map((c, i) => {
    const tr = 4200 - i * 480;
    return {
      customerId: uid('cu', i + 1),
      customerName: c,
      taxId: `2040${String(10000 + i)}`,
      currentManager: { userId: uid('mg', (i % 3) + 1), displayName: MANAGERS[i % 3] },
      totalReceivable: scale2(tr),
      overdueAmount: scale2(tr * 0.35),
      advance: i === 3 ? scale2(-620) : '0.00',
      netBalance: scale2(i === 3 ? -620 : tr),
      maxOverdueDays: [0, 12, 47, 0, 93, 21][i] ?? 0,
      openDocumentCount: 3 + (i % 4),
      dueDateSources: scaleAging(DUE_SOURCES(tr), factor),
      plan: PLAN_BY_ROW[i] ?? NO_PLAN_INDICATOR,
      status: i === 5 ? 'inactive' : 'active',
    };
  });

  // ── Slice W1-A — alerts/tasks fixture (0094's fn_alert_task_* surface) ──
  // /alerts is the app's landing screen now; a demo that opened on PREVIEW_RPC_NOT_IMPLEMENTED
  // would wreck exactly the first impression the redirect exists for. Same conventions as every
  // fixture above: deterministic fixed dates (the page computes days-overdue against the real
  // today, so OVERDUE rows simply age — the June-2026 payment dates behave identically), and the
  // customers/plans referenced agree with the R1/plan fixtures (ვისოლ 93 დღე, ბერიძე 47 დღე,
  // ორბი has the open review, ნიკორა is the customer on a plan).
  const ALERT_THRESHOLDS = { overdueDaysMin: 7, upcomingDaysAhead: 3 };
  const alertTask = (n: number, kind: string, status: string, custIdx: number, dueDate: string | null, extra: Row = {}): Row => ({
    taskId: uid('at', n),
    kind,
    status,
    customerId: uid('cu', custIdx),
    customerName: CUSTOMERS[custIdx - 1] ?? null,
    saleDocumentId: null,
    openingDocumentId: null,
    planId: null,
    planInstallmentId: null,
    reviewFlagId: null,
    commissionEventId: null,
    dueDate,
    // 0095/D-124 defaults: no amount unless the row's `extra` names one (the real RPC's null —
    // "no amount applies" — is the fixture default too, so PLAN_REVIEW/COMMISSION_APPROVAL rows
    // stay honest without repeating the nulls at every call site).
    openAmount: null,
    amountSource: null,
    createdAt: `2026-06-${String(20 + n).padStart(2, '0')}T09:00:00Z`,
    actedAt: null,
    actedBy: null,
    actedByName: null,
    ...extra,
  });
  const alertTasks: Row[] = [
    alertTask(1, 'OVERDUE', 'PROPOSED', Math.min(5, nCust), '2026-05-04', {
      saleDocumentId: uid('sd', 5),
      openAmount: scale2(4300),
      amountSource: 'FIFO_ASOF',
    }),
    alertTask(2, 'OVERDUE', 'ACKNOWLEDGED', 3, '2026-06-19', {
      saleDocumentId: uid('sd', 3),
      openAmount: scale2(1800),
      amountSource: 'FIFO_ASOF',
    }),
    alertTask(3, 'UPCOMING_INSTALLMENT', 'PROPOSED', 2, '2026-12-28', {
      planId: uid('pl', 1),
      planInstallmentId: uid('pi', 3),
      openAmount: scale2(950),
      amountSource: 'PLAN_REMAINING',
    }),
    alertTask(4, 'PLAN_REVIEW', 'PROPOSED', 4, null, { reviewFlagId: uid('rf', 1), planId: uid('pl', 1) }),
    alertTask(5, 'COMMISSION_APPROVAL', 'PROPOSED', 1, null, { commissionEventId: uid('ce', 1) }),
    // DONE over a since-settled invoice: openAmount stays null — the amount column must show
    // absence, never 0 (the 0095 contract the page renders).
    alertTask(6, 'OVERDUE', 'DONE', 2, '2026-06-01', {
      saleDocumentId: uid('sd', 2),
      actedAt: '2026-06-28T14:00:00Z',
      actedBy: PREVIEW_ACTOR_ID,
      actedByName: 'დემო მომხმარებელი',
    }),
  ].slice(0, factor > 0.9 ? 6 : 4);
  const alertCountsOf = (items: readonly Row[]): Row => {
    const byKind: Record<string, number> = {};
    for (const it of items) byKind[String(it['kind'])] = (byKind[String(it['kind'])] ?? 0) + 1;
    // byStatus over ALL tasks, not the filtered set — the page's headline "ღია დავალება" count
    // reads PROPOSED+ACKNOWLEDGED from here and must not zero out when the user filters to
    // "შესრულებული"; the real RPC's counts behave the same way (whole scope, not the page).
    const byStatus: Record<string, number> = {};
    for (const it of alertTasks) byStatus[String(it['status'])] = (byStatus[String(it['status'])] ?? 0) + 1;
    return { total: items.length, byKind, byStatus };
  };

  // Slice P2 — bank import demo rows (BankAssignStep). Suggestions reference customersTable's
  // ids/tax ids so a confirmed suggestion and the customers dropdown agree; the mangled payer
  // names demonstrate the B3 cleaning suggestion in the create-customer form. Handlers are
  // stateless (the step keeps its own optimistic state after each accepted call), matching the
  // static-demo-data convention of every other rpc handler here.
  const bankImportRows = [
    {
      source_row_number: 16, transaction_date: '2026-08-11', document_number: '57294',
      amount: '3200.00', currency: 'GEL', payer_name_raw: 'შპს  გემა', payer_tax_id_raw: '405185222',
      operation_description: 'გადახდა ინვოისით 1042', parser_outcome: 'VALID_NEW',
      posting_status: 'NOT_POSTED', assignable: true, assigned: null,
      suggested: { customer_id: uid('cu', 1), name: CUSTOMERS[0], tax_id: '405185222' },
    },
    {
      source_row_number: 17, transaction_date: '2026-08-11', document_number: '1786008414',
      amount: '1250.50', currency: 'GEL', payer_name_raw: 'ი/მ დავით ბერიძე -------', payer_tax_id_raw: '204954321',
      operation_description: 'დავალიანების დაფარვა', parser_outcome: 'VALID_NEW',
      posting_status: 'NOT_POSTED', assignable: true, assigned: null,
      suggested: { customer_id: uid('cu', 3), name: CUSTOMERS[2], tax_id: '204954321' },
    },
    {
      source_row_number: 18, transaction_date: '2026-08-12', document_number: 'a78911eb-90df',
      amount: '4000.00', currency: 'GEL', payer_name_raw: 'შპს ახალი პარტნიორი', payer_tax_id_raw: '215009988',
      operation_description: 'ავანსი მომავალი მიწოდებისთვის', parser_outcome: 'UNKNOWN_PAYER',
      posting_status: 'NOT_POSTED', assignable: true, assigned: null, suggested: null,
    },
    {
      source_row_number: 19, transaction_date: '2026-08-12', document_number: '88213',
      amount: '780.25', currency: 'GEL', payer_name_raw: 'სამედიცინო დაზღვევა ჯგუფი', payer_tax_id_raw: null,
      operation_description: 'სამედიცინო ხარჯის ანაზღაურება', parser_outcome: 'UNKNOWN_PAYER',
      posting_status: 'NOT_POSTED', assignable: true, assigned: null, suggested: null,
    },
    {
      source_row_number: 20, transaction_date: '2026-08-13', document_number: '90311',
      amount: '5000.00', currency: 'GEL', payer_name_raw: 'შპს ალფა ტრეიდი (საკუთარი)', payer_tax_id_raw: '204876543',
      operation_description: 'შიდა გადარიცხვა ფილიალიდან', parser_outcome: 'INTERNAL_TRANSFER',
      posting_status: 'NOT_POSTED', assignable: false, assigned: null, suggested: null,
    },
    {
      source_row_number: 21, transaction_date: '2026-08-13', document_number: '57295',
      amount: '1250.50', currency: 'GEL', payer_name_raw: 'შპს ორბი ჯგუფი', payer_tax_id_raw: '406112233',
      operation_description: 'განმეორებითი გადახდა', parser_outcome: 'NEAR_DUPLICATE',
      posting_status: 'NOT_POSTED', assignable: true, assigned: null,
      suggested: { customer_id: uid('cu', 4), name: CUSTOMERS[3], tax_id: '406112233' },
    },
  ];

  const rpc: Record<string, (p: Row) => unknown> = {
    // Slice P2 — bank import (0104): list answers the fixed demo batch; assign/commit accept and
    // answer the RPCs' real success shapes (static demo data is not mutated).
    fn_bank_import_list_rows: () => ({
      ok: true,
      batch: { id: 'fixture-bank-1', status: 'PREVIEW', source_file_name: 'Operations_demo.xlsx' },
      rows: bankImportRows,
    }),
    fn_bank_import_assign_customer: () => ({ ok: true }),
    // D-246 (0154) — explicit skip and the late posting into a COMMITTED batch.
    fn_bank_import_set_skip: () => ({ ok: true }),
    fn_bank_import_commit_remaining: () => ({
      ok: true,
      batch_id: 'fixture-bank-1',
      posted_count: 1,
      skipped_count: 0,
      total_posted_amount: '3200.00',
    }),
    // Slice P1-F-1d-api (D-206) — the CHANGED_EXISTING resolution pair (0127/0128/0129). Anchored
    // to the row the sales preview fixture ALREADY shows as changed (importGatewayFixture.ts:85 —
    // row 1450, SD-3510, 650.00), so the demo never invents a second version of that document.
    // ⚠️ The preview client hands handlers only `p_payload` (client.ts:438), and these RPCs use
    // named scalar args instead, so the answer is the same for every row — exactly like the bank
    // assign mock above. It exists so the screen is walkable, not to simulate the guards.
    fn_import_get_change_comparison: () => ({
      ok: true,
      source_row_number: 1450,
      parser_outcome: 'CHANGED_EXISTING',
      change_resolution_status: null,
      source_document_id: 'SD-3510',
      sales_document_id: 'fixture-sales-3510',
      current_normalized: {
        sourceDocumentId: 'SD-3510', waybillNumber: 'SD-3510', sourceStatus: 'ACTIVE',
        documentType: 'SALE', organization: { rawText: 'შპს ეპსილონი', customerTaxId: '405185222' },
        sourceAmount: '650.00', sourceAmountMinorUnits: '65000', financialEffectMinorUnits: '65000',
        documentDate: '2026-07-14', cancelledAt: null, invoiceReferenceId: '356936660',
        deliveryState: 'მიღებული', sourceCategory: 'ჩვეულებრივი', sourceInternalStatus: 'A',
      },
      original_normalized: {
        sourceDocumentId: 'SD-3510', waybillNumber: 'SD-3510', sourceStatus: 'ACTIVE',
        documentType: 'SALE', organization: { rawText: 'შპს ეპსილონი', customerTaxId: '405185222' },
        sourceAmount: '580.00', sourceAmountMinorUnits: '58000', financialEffectMinorUnits: '58000',
        documentDate: '2026-07-14', cancelledAt: null, invoiceReferenceId: '356936660',
        deliveryState: 'მიღებული', sourceCategory: 'ჩვეულებრივი', sourceInternalStatus: 'A',
      },
    }),
    fn_import_resolve_changed_existing: () => ({
      ok: true,
      action: 'correct',
      source_row_number: 1450,
      sales_document_id: 'fixture-sales-3510',
      change_resolution_status: 'CORRECTED',
      resolution: { ok: true, kind: 'correction' },
    }),
    fn_bank_import_commit: () => ({
      ok: true,
      batch_id: 'fixture-bank-1',
      posted_count: 3,
      skipped_count: 3,
      total_posted_amount: '5230.75',
    }),
    // Slice K-M — the monthly overview pair (0114). Static demo data in the RPCs' real shapes;
    // the scope list mirrors what a company-wide caller would get, so the "ჩემი / ყველა" switch
    // and the recipient picker can both be walked in Preview Mode.
    // K-E-2c — create (Draft, D5-style code) then calculate (Draft → Calculated). In-memory, so the
    // create-and-calculate flow and the resulting list row can both be walked in Preview Mode.
    fn_commission_create_batch: (p) => {
      const recIdx = Math.max(0, RECIPIENTS.findIndex((_, i) => uid('re', i + 1) === String(p['recipientId'])));
      const id = uid('ba', batches.length + 1);
      const code = `COMM-${String(p['periodFrom']).slice(0, 7)}-${String(batches.length + 1).padStart(3, '0')}`;
      batches.unshift({
        id, company_id: companyId, code, status: 'Draft',
        period_from: p['periodFrom'], period_to: p['periodTo'],
        calculated_total: '0.0000', adjustments_total: '0.0000', payable_total: '0.0000', paid_total: '0.0000',
        paid_at: null, payment_document_ref: null, approved_by: null, approved_at: null,
        created_at: '2026-06-30T12:00:00Z',
        recipient_id: uid('re', recIdx + 1),
        commission_recipients: { display_name: RECIPIENTS[recIdx] },
      });
      return { ok: true, batch_id: id, code };
    },
    fn_commission_calculate_batch: (p) => {
      const b = batches.find((x) => x['id'] === p['batchId']) as Record<string, unknown> | undefined;
      if (b === undefined) return { ok: false };
      const mk = (n: number, type: string, amount: number, cust: number): Row => ({
        id: uid('ln', 900 + batches.length * 10 + n), company_id: companyId, batch_id: b['id'],
        source_type: type, source_id: uid('ev', 90 + n), amount, included_at: '2026-06-30T12:00:00Z',
        customer_id: uid('cu', cust + 1), customers: { name: CUSTOMERS[cust] },
      });
      lines.push(mk(1, 'event', 552, 0), mk(2, 'event', 217.515, 1), mk(3, 'adjustment', -30, 0));
      Object.assign(b, { status: 'Calculated', calculated_total: '769.5150', adjustments_total: '-30.0000', payable_total: '739.5150' });
      return { ok: true, batch_id: b['id'], calculated_total: '769.5150', adjustments_total: '-30.0000', payable_total: '739.5150' };
    },
    // K-E-2c-3 (D-192) — informational advance per batch customer, in the RPC's real shape. The
    // first demo customer carries an advance and the second none, so the badge and its absence are
    // both visible in Preview Mode.
    fn_commission_settlement_advances: (p) => {
      const ids = [...new Set(lines.filter((l) => l['batch_id'] === p['batchId'] || p['batchId'] === undefined)
        .map((l) => l['customer_id']).filter((c): c is string => typeof c === 'string'))].sort();
      return {
        ok: true,
        rows: ids.map((id) => ({ customer_id: id, advance_amount: id === uid('cu', 1) ? scale(420) : '0.0000' })),
      };
    },
    fn_commission_scope_recipient_list: () => ({
      ok: true,
      recipients: [
        { id: uid('re', 1), display_name: RECIPIENTS[0] },
        { id: uid('re', 2), display_name: RECIPIENTS[1] },
      ],
    }),
    fn_commission_monthly_overview: () => ({
      ok: true,
      scope: 'company',
      rows: [
        {
          customer_id: uid('cu', 1), customer_name: CUSTOMERS[0],
          recipient_id: uid('re', 1), recipient_name: RECIPIENTS[0],
          collected_amount: '18400.0000', commission_amount: '552.0000',
        },
        {
          customer_id: uid('cu', 2), customer_name: CUSTOMERS[1],
          recipient_id: uid('re', 1), recipient_name: RECIPIENTS[0],
          collected_amount: '7250.5000', commission_amount: '217.5150',
        },
        {
          customer_id: uid('cu', 4), customer_name: CUSTOMERS[3],
          recipient_id: uid('re', 2), recipient_name: RECIPIENTS[1],
          collected_amount: '4100.0000', commission_amount: '205.0000',
        },
      ],
      totals: { collected_amount: '29750.5000', commission_amount: '974.5150' },
    }),
    // Slice K-D-UI — the commission backfill pair (0109/0110). Static demo data in the RPCs' real
    // shapes, so the three-step screen (preview → confirm → result) can be walked end to end in
    // Preview Mode; nothing is mutated, so a second run answers identically.
    fn_commission_backfill_preview: () => ({
      ok: true,
      customer_id: uid('cu', 1),
      documents_to_backfill: 2,
      allocations_to_recompute: 3,
      expected_commission_total: '160.0000',
      settlement_closed: false,
      settlement_warning: null,
      documents: [
        {
          sales_document_id: uid('sd', 901), document_number: 'A-77301', document_date: '2026-07-04',
          amount: '1000.0000', currency: 'GEL', allocated_amount: '1000.0000',
          recipients: [{ recipient_id: uid('re', 1), recipient_name: RECIPIENTS[0], percent: '10.000000', expected_amount: '100.0000' }],
        },
        {
          sales_document_id: uid('sd', 902), document_number: 'A-77318', document_date: '2026-07-15',
          amount: '900.0000', currency: 'GEL', allocated_amount: '600.0000',
          recipients: [{ recipient_id: uid('re', 1), recipient_name: RECIPIENTS[0], percent: '10.000000', expected_amount: '60.0000' }],
        },
      ],
    }),
    fn_commission_backfill_customer: () => ({
      ok: true,
      customer_id: uid('cu', 1),
      documents_backfilled: 2,
      commission_total: '160.0000',
      affected_from: '2026-07-04',
      recomputes: [],
    }),
    // Slice P1-A — fn_customer_create (0098): preview accepts the payload and answers the RPC's
    // real success shape; the list is static demo data, so the new row is not appended.
    fn_customer_create: (p) => ({
      ok: true,
      customer_id: uid('cu', 900),
      party_id: uid('pa', 900),
      party_created: true,
      normalized_tax_id: (typeof p['p_tax_id'] === 'string' ? p['p_tax_id'] : '').replace(/[\s-]/g, ''),
    }),
    // Slice P1-H-2 — fn_customer_update (0101): same convention — accept, answer the RPC's real
    // success shape; the static list itself is not mutated.
    fn_customer_update: (p) => ({
      ok: true,
      customer_id: p['p_customer_id'],
      old: {},
      new: {},
    }),
    // Slice P3 — opening-balance RPCs (0102): accept, answer the real success shapes; the static
    // committed list is not mutated (the create/approve/commit/cancel/amend flows demo end-to-end).
    fn_create_opening_batch: (p) => ({
      ok: true,
      batch_id: uid('ob', 900),
      documents: Array.isArray((p['p_payload'] as Record<string, unknown> | undefined)?.['rows'])
        ? ((p['p_payload'] as Record<string, unknown>)['rows'] as unknown[]).length
        : 0,
      status: 'VALIDATED',
    }),
    fn_approve_opening_batch: (p) => ({
      ok: true,
      batch_id: (p['p_payload'] as Record<string, unknown> | undefined)?.['batch_id'],
      status: 'APPROVED',
    }),
    fn_commit_opening_batch: (p) => ({
      ok: true,
      batch_id: (p['p_payload'] as Record<string, unknown> | undefined)?.['batch_id'],
      status: 'COMMITTED',
      posted: 1,
      idempotent: false,
    }),
    fn_cancel_opening_batch: (p) => ({
      ok: true,
      batch_id: (p['p_payload'] as Record<string, unknown> | undefined)?.['batch_id'],
      status: 'CANCELLED',
      discarded: 1,
    }),
    fn_amend_opening_document: (p) => ({
      ok: true,
      old_document_id: (p['p_payload'] as Record<string, unknown> | undefined)?.['opening_document_id'],
      new_document_id: uid('od', 901),
      old_effect: '0',
      new_amount: (p['p_payload'] as Record<string, unknown> | undefined)?.['new_amount'],
    }),
    fn_report_debtor_summary: () => ({
      rows: r1rows,
      totals: {
        customerCount: nCust,
        totalReceivable: scale2(18400),
        overdueAmount: scale2(6100),
        advance: scale2(-620),
        dueDateSources: scaleAging(DUE_SOURCES(18400), factor),
        plan: planTotals(nCust),
      },
      page,
    }),
    // P1-H-3: export audit sink (0026) — Preview writes nothing, it only returns an audit id.
    fn_log_report_export: () => uid('ae', 9001),
    fn_report_customer_statement: () => ({
      rows: [
        // P1-H-3: sourceRefType/sourceRefId (real RPC fields, 0107:102-103) let the Excel export's
        // paid/unpaid subsets resolve each SALE against the `sales_documents` table below.
        { ledgerEntryId: uid('le', 1), effectiveDate: '2026-06-01', recordedAt: '2026-06-01T10:00:00Z', type: 'SALE', sourceRefType: 'sales_document', sourceRefId: uid('sd', 1001), reference: 'SF-2026-1001', debit: scale2(1200), credit: null, runningBalance: scale2(1200) },
        { ledgerEntryId: uid('le', 2), effectiveDate: '2026-06-08', recordedAt: '2026-06-08T10:00:00Z', type: 'INCOMING_PAYMENT', sourceRefType: 'incoming_payment', sourceRefId: uid('ip', 88213), reference: 'BNK-88213', debit: null, credit: scale2(700), runningBalance: scale2(500) },
        { ledgerEntryId: uid('le', 3), effectiveDate: '2026-06-15', recordedAt: '2026-06-15T10:00:00Z', type: 'SALE', sourceRefType: 'sales_document', sourceRefId: uid('sd', 1044), reference: 'SF-2026-1044', debit: scale2(900), credit: null, runningBalance: scale2(1400) },
        { ledgerEntryId: uid('le', 4), effectiveDate: '2026-06-22', recordedAt: '2026-06-22T10:00:00Z', type: 'RETURN', sourceRefType: 'sales_document', sourceRefId: uid('sd', 1045), reference: 'SF-2026-1044', debit: null, credit: scale2(150), runningBalance: scale2(1250) },
      ],
      totals: {
        openingBalance: scale2(0),
        totalDebit: scale2(2100),
        totalCredit: scale2(850),
        closingBalance: scale2(1250),
        openDocumentCount: 2,
        openAmount: scale2(1250),
      },
      page: { returned: 4, limit: 50, hasMore: false },
    }),
    // 7.10-B (D-Z): the customer page's plan block. Preview shows a customer WITH a plan so the
    // KPI section is visible; `plan: null` is the ordinary case for everyone else.
    fn_report_customer_plan: () => ({
      plan: {
        plan_id: uid('pl', 1),
        customer_id: uid('cu', 1),
        status: 'ACTIVE',
        currency: 'GEL',
        principal_amount: scale2(3600),
        template_id: uid('tp', 1),
        superseded_by_plan_id: null,
        activated_at: '2026-06-01T09:00:00Z',
        is_breached: true,
        kpi: {
          paid_amount: scale2(1200),
          remaining_amount: scale2(2400),
          delay_days: 23,
          next_installment: {
            installment_id: uid('in', 2),
            seq: 2,
            due_date: '2026-07-10',
            amount: scale2(1200),
            paid_amount: scale2(0),
            remaining_amount: scale2(1200),
          },
        },
        installments: [1, 2, 3].map((seq) => ({
          installment_id: uid('in', seq),
          seq,
          amount: scale2(1200),
          paid_amount: seq === 1 ? scale2(1200) : scale2(0),
          nominal_due_date: `2026-0${5 + seq}-10`,
          due_date: `2026-0${5 + seq}-10`,
          adjustment_reason: 'NONE',
          calendar_version_id: null,
          derived_status: seq === 1 ? 'paid' : 'unpaid',
          is_overdue: seq === 2,
        })),
      },
      openReviewCount: 1,
    }),
    fn_report_open_documents: () => ({
      rows: CUSTOMERS.slice(0, nCust).map((c, i) => ({
        documentId: uid('od', i + 1),
        sourceType: i === 5 ? 'opening_document' : 'sales_document',
        customerId: uid('cu', i + 1),
        customerName: c,
        documentNumber: `SF-2026-${1001 + i}`,
        documentDate: `2026-05-${String(10 + i).padStart(2, '0')}`,
        effectiveDueDate: `2026-06-${String(10 + i).padStart(2, '0')}`,
        dueDateSource: DUE_SOURCE_BY_ROW[i] ?? 'LEGACY_FALLBACK',
        dueDateRunKind: DUE_RUN_BY_ROW[i] ?? null,
        openAmount: scale2(1400 - i * 120),
        overdueDays: [0, 12, 47, 0, 93, 21][i] ?? 0,
        agingBucket: ['notDue', 'd8_15', 'd31_60', 'notDue', 'd91_plus', 'd16_30'][i] ?? 'notDue',
        isOpening: i === 5,
        status: 'open',
      })),
      totals: {
        documentCount: nCust,
        openAmount: scale2(6800),
        overdueAmount: scale2(3200),
        dueDateSources: scaleAging(DUE_SOURCES(6800), factor),
      },
      page,
    }),
    fn_report_aging: () => ({
      rows: CUSTOMERS.slice(0, nCust).map((c, i) => {
        const t = 3600 - i * 400;
        return { customerId: uid('cu', i + 1), customerName: c, manager: { userId: uid('mg', (i % 3) + 1), displayName: MANAGERS[i % 3] }, aging: scaleAging(AGING(t), factor), dueDateSources: scaleAging(DUE_SOURCES(t), factor), plan: PLAN_BY_ROW[i] ?? NO_PLAN_INDICATOR, total: scale2(t) };
      }),
      totals: {
        customerCount: nCust,
        total: scale2(15200),
        aging: scaleAging(AGING(15200), factor),
        dueDateSources: scaleAging(DUE_SOURCES(15200), factor),
        plan: planTotals(nCust),
      },
      page,
    }),
    fn_report_payments: () => {
      const rows = [0, 1, 2, 3].map((i) => ({
        paymentId: uid('pm', i + 1),
        transactionDate: `2026-06-${String(3 + i * 5).padStart(2, '0')}`,
        customerId: uid('cu', i + 1),
        customerName: CUSTOMERS[i],
        payerName: i === 2 ? 'შპs მესამე პირი' : null,
        isThirdParty: i === 2,
        amount: scale2(900 - i * 90),
        allocatedAmount: scale2(700 - i * 90),
        unallocatedAmount: scale2(i === 0 ? 200 : 0),
        postingStatus: ['POSTED', 'POSTED', 'PENDING', 'UNMATCHED'][i] ?? 'POSTED',
        bankDocumentNumber: `BNK-${88200 + i}`,
      }));
      return {
        rows,
        totals: {
          paymentCount: rows.length,
          amount: sumMoney2(rows.map((r) => r.amount)),
          allocatedAmount: sumMoney2(rows.map((r) => r.allocatedAmount)),
          unallocatedAmount: sumMoney2(rows.map((r) => r.unallocatedAmount)),
        },
        page: { returned: 4, limit: 50, hasMore: false },
      };
    },
    fn_report_advances: () => ({
      rows: [0, 1].map((i) => ({
        creditSourceType: ['payment', 'opening_advance'][i] ?? 'payment',
        creditSourceId: uid('ad', i + 1),
        sourceReference: `BNK-${88200 + i}`,
        customerId: uid('cu', 4),
        customerName: CUSTOMERS[3],
        manager: { userId: uid('mg', 1), displayName: MANAGERS[0] },
        originalCredit: scale2(800 - i * 200),
        allocatedAmount: scale2(180),
        refundedAmount: '0.00',
        availableAmount: scale2(620 - i * 200),
        effectiveDate: `2026-06-${String(4 + i).padStart(2, '0')}`,
        lastAppliedDate: i === 0 ? '2026-06-20' : null,
      })),
      totals: { sourceCount: 2, originalCredit: scale2(1400), availableAmount: scale2(1040) },
      page: { returned: 2, limit: 50, hasMore: false },
    }),
    fn_report_exceptions: () => ({
      rows: [
        { kind: 'unreconciled_opening', entityType: 'opening_document', entityId: uid('od', 9), customerId: uid('cu', 5), customerName: CUSTOMERS[4], effectiveDate: '2026-01-01', amount: scale2(2400), reason: 'opening_reconciliation_required', actorUserId: null, approverUserId: null, recomputeRunId: null, recordedAt: '2026-06-02T10:00:00Z' },
        { kind: 'refund_override', entityType: 'refund', entityId: uid('rf', 1), customerId: uid('cu', 2), customerName: CUSTOMERS[1], effectiveDate: '2026-06-10', amount: scale2(500), reason: 'manager approval', actorUserId: PREVIEW_ACTOR_ID, approverUserId: PREVIEW_ACTOR_ID, recomputeRunId: null, recordedAt: '2026-06-10T11:00:00Z' },
        { kind: 'correction', entityType: 'sales_document', entityId: uid('sd', 3), customerId: uid('cu', 3), customerName: CUSTOMERS[2], effectiveDate: '2026-06-12', amount: null, reason: 'price correction', actorUserId: PREVIEW_ACTOR_ID, approverUserId: PREVIEW_ACTOR_ID, recomputeRunId: uid('rr', 1), recordedAt: '2026-06-12T09:00:00Z' },
        { kind: 'recompute', entityType: 'recompute_run', entityId: uid('rr', 2), customerId: uid('cu', 1), customerName: CUSTOMERS[0], effectiveDate: '2026-06-14', amount: null, reason: 'customer_recompute', actorUserId: PREVIEW_ACTOR_ID, approverUserId: null, recomputeRunId: uid('rr', 2), recordedAt: '2026-06-14T09:00:00Z' },
      ],
      totals: { count: 4, byKind: { correction: 1, cancellation: 0, restoration: 0, customer_reassignment: 0, refund: 0, refund_override: 1, recompute: 1, unreconciled_opening: 1, review_item: 0 } },
      page: { returned: 4, limit: 50, hasMore: false },
    }),
    fn_report_manager_summary: () => {
      const rows = MANAGERS.map((m, i) => {
        const tr = 6200 - i * 900;
        // P1-H-2 (PO5): the first manager is the users-table sales_manager (uid('mb',1)) — no
        // badge; the rest keep synthetic ids and demo the „არააქტიური" badge (frozen debt whose
        // manager is no longer an active sales_manager).
        return { manager: { userId: i === 0 ? uid('mb', 1) : uid('mg', i + 1), displayName: m }, customerCount: 2 + i, totalReceivable: scale2(tr), overdueAmount: scale2(tr * 0.3), advance: scale2(i === 2 ? -400 : 0), collections: scale2(1800 - i * 300), aging: scaleAging(AGING(tr), factor) };
      });
      return {
        rows,
        totals: {
          managerCount: rows.length,
          totalReceivable: sumMoney2(rows.map((r) => r.totalReceivable)),
          overdueAmount: sumMoney2(rows.map((r) => r.overdueAmount)),
          advance: sumMoney2(rows.map((r) => r.advance)),
          collections: sumMoney2(rows.map((r) => r.collections)),
        },
        page: { returned: 3, limit: 50, hasMore: false },
      };
    },
    fn_commission_analytics: (p: Row) => {
      const groupBy = typeof p['groupBy'] === 'string' && p['groupBy'] !== '' ? p['groupBy'] : 'recipient';
      const labels =
        groupBy === 'customer'
          ? CUSTOMERS.slice(0, 4)
          : groupBy === 'period'
            ? ['2026-04', '2026-05', '2026-06']
            : RECIPIENTS.slice(0, 3);
      const rows = labels.map((label, i) => {
        const accrued = 900 - i * 180;
        const adjusted = i === 1 ? -120 : 0;
        return {
          groupKey: label,
          groupId: groupBy === 'customer' ? uid('cu', i + 1) : groupBy === 'period' ? null : uid('re', i + 1),
          accrued: scale(accrued),
          approved: scale(accrued * 0.8),
          payable: scale(accrued + adjusted),
          paid: scale(i === 2 ? accrued : 0),
          adjusted: scale(adjusted),
          returnReduced: scale(i === 1 ? -120 : 0),
        };
      });
      const sum = (k: string): string =>
        rows.reduce((t, r) => t + Number((r as Record<string, string>)[k]), 0).toFixed(4);
      return {
        rows,
        totals: {
          accrued: sum('accrued'),
          approved: sum('approved'),
          payable: sum('payable'),
          paid: sum('paid'),
          adjusted: sum('adjusted'),
          returnReduced: sum('returnReduced'),
        },
      };
    },
    fn_commission_dashboard: () => ({
      currentMonth: { accrued: scale(1840), payable: scale(1790), paid: scale(900) },
      pendingApprovalCount: 2,
      pendingSettlement: scale(2100),
      adjustments: { count: adjustments.length, total: scale(-390) },
      topRecipients: recipients.slice(0, 3).map((r, i) => ({ recipientId: r['id'], displayName: r['display_name'], payable: scale(720 - i * 180) })),
      topCustomers: CUSTOMERS.slice(0, 3).map((c, i) => ({ customerId: uid('cu', i + 1), customerName: c, payable: scale(640 - i * 150) })),
      recentBatches: batches.slice(0, 3).map((b) => ({ code: b['code'], status: b['status'], payable: b['payable_total'] })),
    }),
    fn_alert_task_thresholds: () => ALERT_THRESHOLDS,
    fn_alert_tasks_refresh: () => ({ scope: 'company', thresholds: ALERT_THRESHOLDS, proposed: 0, withdrawn: 0 }),
    fn_alert_task_list: (p: Row) => {
      const f = (p['filters'] as Row | undefined) ?? {};
      // The RPC's own default IS the open queue (0094) — the page relies on that, not on
      // restating it, so the fixture mirrors it here.
      const statuses = (f['status'] as readonly string[] | undefined) ?? ['PROPOSED', 'ACKNOWLEDGED'];
      const byStatus = alertTasks.filter((x) => statuses.includes(String(x['status'])));
      const kind = f['kind'];
      const items = typeof kind === 'string' && kind !== '' ? byStatus.filter((x) => x['kind'] === kind) : byStatus;
      return { scope: 'company', thresholds: ALERT_THRESHOLDS, counts: alertCountsOf(items), items };
    },
  };

  const data: CompanyData = {
    tables,
    rpc,
    transition(batchId: string, to: string): void {
      const b = batches.find((x) => x['id'] === batchId);
      if (b !== undefined) (b as Record<string, unknown>)['status'] = to;
    },
    alertAction(taskId: string, action: 'acknowledge' | 'done' | 'dismiss'): Row {
      const next = action === 'acknowledge' ? 'ACKNOWLEDGED' : action === 'done' ? 'DONE' : 'DISMISSED';
      const task = alertTasks.find((x) => x['taskId'] === taskId);
      const previous = task !== undefined ? String(task['status']) : '';
      if (task !== undefined) {
        task['status'] = next;
        task['actedAt'] = '2026-06-30T12:00:00Z';
        task['actedBy'] = PREVIEW_ACTOR_ID;
        task['actedByName'] = 'დემო მომხმარებელი';
      }
      return {
        ok: task !== undefined,
        taskId,
        kind: task !== undefined ? String(task['kind']) : '',
        customerId: task !== undefined ? String(task['customerId']) : '',
        previousStatus: previous,
        status: next,
      };
    },
  };
  return data;
}

function overrideRowsMerge(events: Row[], overrides: Row[]): Row[] {
  // commission_events reads need the full override columns present; merge them in.
  return events.map((e) => {
    const o = overrides.find((x) => x['id'] === e['id']);
    return o ?? { ...e, base_override_comment: null, base_overridden_by: null, base_approved_by: null, base_approved_at: null };
  });
}

function scaleAging(a: Record<string, string>, factor: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(a)) out[k] = (Number(a[k]) * factor).toFixed(4);
  return out;
}

// Exact-decimal sum for 2dp money strings (as produced by scale2 above) — BigInt cents, never
// Number/parseFloat, so a totals row is always the true sum of the rows it summarizes, including
// negative values (e.g. advance).
function toCents2(value: string): bigint {
  const negative = value.startsWith('-');
  const unsigned = negative ? value.slice(1) : value;
  const [intPart = '', fracPart = ''] = unsigned.split('.');
  const cents = BigInt(intPart === '' ? '0' : intPart) * 100n + BigInt((fracPart + '00').slice(0, 2));
  return negative ? -cents : cents;
}
function fromCents2(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const intPart = (abs / 100n).toString();
  const fracPart = (abs % 100n).toString().padStart(2, '0');
  return `${negative && abs !== 0n ? '-' : ''}${intPart}.${fracPart}`;
}
function sumMoney2(values: readonly string[]): string {
  return fromCents2(values.reduce((acc, v) => acc + toCents2(v), 0n));
}

const DATA: Record<string, CompanyData> = {
  [PREVIEW_COMPANIES[0]!.id]: build(PREVIEW_COMPANIES[0]!.id, 1),
  [PREVIEW_COMPANIES[1]!.id]: build(PREVIEW_COMPANIES[1]!.id, 0.6),
};

export function previewData(companyId: string): CompanyData {
  return DATA[companyId] ?? DATA[PREVIEW_COMPANIES[0]!.id]!;
}

// ── K-C-b external parties (0122/0123) ──────────────────────────────────────────────────────────
// fn_party_search_external / fn_party_create_external take positional args (p_query, p_name, …),
// not p_payload, so client.ts routes them here directly. Deterministic, company-independent demo
// parties; create echoes a new in-memory party (no persistence beyond the page session).
const PREVIEW_PARTIES: Row[] = [
  { party_id: uid('pa', 1), name: 'შპს ბიზნეს სააგენტო', party_type: 'legal_entity', tax_id_raw: '204566978', normalized_tax_id: '204566978' },
  { party_id: uid('pa', 2), name: 'ლევან ჩხეიძე', party_type: 'natural_person', tax_id_raw: '01024056789', normalized_tax_id: '01024056789' },
  { party_id: uid('pa', 3), name: 'შპს პარტნიორ ტრეიდ', party_type: 'partner_company', tax_id_raw: '404123456', normalized_tax_id: '404123456' },
  { party_id: uid('pa', 4), name: 'თამარ ბერიძე (კონსულტანტი)', party_type: 'other', tax_id_raw: null, normalized_tax_id: null },
];

export function previewPartyRpc(fn: string, args: Record<string, unknown>): Row | undefined {
  if (fn === 'fn_party_search_external') {
    const q = typeof args['p_query'] === 'string' ? args['p_query'].trim().toLowerCase() : '';
    if (q === '') return { ok: true, items: [] };
    const code = q.replace(/[\s-]/g, '');
    return {
      ok: true,
      items: PREVIEW_PARTIES.filter((p) =>
        String(p['name']).toLowerCase().includes(q) ||
        (/^[0-9]+$/.test(code) && typeof p['normalized_tax_id'] === 'string' && p['normalized_tax_id'].startsWith(code)),
      ).slice(0, 20),
    };
  }
  if (fn === 'fn_party_create_external') {
    const party: Row = {
      party_id: uid('pa', PREVIEW_PARTIES.length + 1),
      name: typeof args['p_name'] === 'string' ? args['p_name'].trim() : '',
      party_type: args['p_party_type'],
      tax_id_raw: args['p_tax_id'] ?? null,
      normalized_tax_id: typeof args['p_tax_id'] === 'string' ? args['p_tax_id'].replace(/[\s-]/g, '') : null,
    };
    PREVIEW_PARTIES.push(party);
    return { ok: true, party_created: true, ...party };
  }
  return undefined;
}

